import {
  formatCashAmount,
  getTransactionListStatusDisplay,
  type TransactionListAmountColumns,
} from './transactionDisplayUtils';
import type { Transaction } from '../types';
import { getWithdrawalStatusDisplay } from './withdrawalStatusDisplay';

export type WithdrawalPayoutRefundSync = {
  refundedAt?: string | null;
  refundAmount?: number | string | null;
  refundReason?: string | null;
  walletTransactionId?: number | string | null;
  source?: string | null;
  gatewayState?: number | null;
};

export type WithdrawalPayoutRefundDisplay = {
  refundAmount: number;
  refundedAt: string;
  refundTransactionId: string;
  walletTransactionId: number | null;
  source: string | null;
};

export type WithdrawalPayoutRefundInput = Pick<
  Transaction,
  'type' | 'status' | 'transactionId' | 'amount' | 'syncMetadata'
>;

const REFUNDABLE_WITHDRAWAL_STATUSES = new Set(['failed', 'cancelled']);

function parsePositiveAmount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function readPayoutRefundSync(syncMetadata: Record<string, unknown> | null | undefined) {
  const sync = syncMetadata ?? {};
  return (sync.payoutRefund ?? sync.payout_refund) as WithdrawalPayoutRefundSync | undefined;
}

/** Player wallet was credited back after external payout failure (GGUS / LinkMePay-style). */
export function getWithdrawalPayoutRefundInfo(
  tx: WithdrawalPayoutRefundInput | null | undefined,
): WithdrawalPayoutRefundDisplay | null {
  if (!tx || tx.type !== 'withdrawal') return null;

  const status = String(tx.status || '').toLowerCase();
  if (!REFUNDABLE_WITHDRAWAL_STATUSES.has(status)) return null;

  const payoutRefund = readPayoutRefundSync(tx.syncMetadata);
  if (!payoutRefund?.refundedAt) return null;

  const refundAmount = parsePositiveAmount(payoutRefund.refundAmount ?? tx.amount);
  if (refundAmount == null) return null;

  const txnId = String(tx.transactionId || '').trim();
  const walletTransactionIdRaw = payoutRefund.walletTransactionId;
  const walletTransactionId =
    walletTransactionIdRaw != null && walletTransactionIdRaw !== ''
      ? Number(walletTransactionIdRaw)
      : null;

  return {
    refundAmount,
    refundedAt: String(payoutRefund.refundedAt),
    refundTransactionId: txnId ? `refund_${txnId}` : '—',
    walletTransactionId: Number.isFinite(walletTransactionId) ? walletTransactionId : null,
    source: payoutRefund.source != null ? String(payoutRefund.source) : null,
  };
}

export function isWithdrawalPayoutRefunded(
  tx: WithdrawalPayoutRefundInput | null | undefined,
): boolean {
  return getWithdrawalPayoutRefundInfo(tx) != null;
}

/** Admin list/detail badge when payout failed but wallet was refunded. */
export function getAdminTransactionStatusDisplay(
  transaction: Pick<Transaction, 'status' | 'fulfillmentStatus' | 'type' | 'syncMetadata' | 'transactionId' | 'amount'>,
) {
  if (getWithdrawalPayoutRefundInfo(transaction)) {
    return getWithdrawalStatusDisplay('failed_refunded');
  }
  return getTransactionListStatusDisplay(transaction);
}

export function applyWithdrawalPayoutRefundListAmount(
  columns: TransactionListAmountColumns,
  transaction: WithdrawalPayoutRefundInput,
  currency = 'USD',
): TransactionListAmountColumns {
  const refundInfo = getWithdrawalPayoutRefundInfo(transaction);
  if (!refundInfo) return columns;

  const requested = parsePositiveAmount(transaction.amount) ?? refundInfo.refundAmount;
  return {
    ...columns,
    usdLabel: formatCashAmount(0, currency),
    usdTone: 'positive',
    usdDirection: null,
    usdSubLabel: `Requested ${formatCashAmount(requested, currency)} · net $0 after refund`,
  };
}

export function applyWithdrawalPayoutRefundDetailAmount(
  columns: TransactionListAmountColumns,
  transaction: WithdrawalPayoutRefundInput,
  currency = 'USD',
): TransactionListAmountColumns {
  const refundInfo = getWithdrawalPayoutRefundInfo(transaction);
  if (!refundInfo) return columns;

  const requested = parsePositiveAmount(transaction.amount) ?? refundInfo.refundAmount;
  return {
    ...columns,
    usdLabel: formatCashAmount(0, currency),
    usdTone: 'positive',
    usdDirection: null,
    usdSubLabel: `Requested ${formatCashAmount(requested, currency)} · net $0 after refund`,
  };
}

export function formatWithdrawalPayoutRefundSource(source: string | null): string | null {
  if (!source) return null;
  return source.replace(/_/g, ' ');
}
